import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Separator } from '@blade/ui-kit'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui/currency-icon/currency-network-icon'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { AmountFormatted, NumericFormat } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import { CrownOutlinedIcon } from '@root/shared/ui-kit'

import type { PairDevTokensInfo } from '@root/entities/pair/hooks/use-pair-dev-tokens-info'

interface Props {
  devTokensInfo: PairDevTokensInfo
}

export function ExploreDevTokensTooltipCloseArea({ devTokensInfo }: Props) {
  const { t } = useTranslation()

  const [network] = useAtom(selectedNetworkAtom)
  const { creatorStats, bondedTokensWithoutCurrent, totalPoolsWithoutCurrent } =
    devTokensInfo

  return (
    <Root>
      <Title>{t('common.dev-tokens')}</Title>
      <BodySeparator variant="solid" color="var(--backgroundA-3)" />
      <Body>
        {
          <Item>
            <Key>{t('explore.developer-tokens-tooltip.bonded')}</Key>
            <Value>
              <BondedCount>
                {bondedTokensWithoutCurrent}
                <CrownOutlinedIcon size={16} />
              </BondedCount>{' '}
              {creatorStats.bondedPercent === undefined ? undefined : (
                <>
                  {'(' +
                    NumericFormat.formatPreset(
                      creatorStats.bondedPercent,
                      'percents',
                    ) +
                    '%)'}
                </>
              )}
            </Value>
          </Item>
        }
        <Item>
          <Key>{t('explore.developer-tokens-tooltip.created')}</Key>
          <Value>{totalPoolsWithoutCurrent}</Value>
        </Item>
        {creatorStats.created7d === undefined ? undefined : (
          <Item>
            <Key>{t('explore.developer-tokens-tooltip.created-7d')}</Key>
            <Value>{creatorStats.created7d}</Value>
          </Item>
        )}
        {creatorStats.solBalance === undefined ? undefined : (
          <Item>
            <Key>
              {Network.toDisplayString(network).toUpperCase() +
                ' ' +
                t('common.balance')}
            </Key>
            <Value>
              <AmountFormatted
                roundingMode="trunc"
                amount={creatorStats.solBalance}
              />
              <CurrencyNetworkIcon network={network} size={12} />
            </Value>
          </Item>
        )}
      </Body>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
`

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  color: var(--text-12);
`

const Body = styled.ul`
  all: unset;
  display: flex;
  flex-direction: column;
  gap: 5px;

  color: var(--text-10);

  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`
const Item = styled.li`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
`
const Value = styled.div`
  color: var(--text-11);
  display: flex;
  gap: 4px;
  align-items: center;
`
const Key = styled.div``

const BondedCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--amber-9);
`

const BodySeparator = styled(Separator)`
  margin: 8px 0;
`
